import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listLists, createList, deleteList } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Trash2, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Lists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listLists();
      setLists(data);
    } catch (e) {
      toast.error("Failed to load lists");
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("List name is required");
      return;
    }
    try {
      await createList({ name: name.trim(), description });
      toast.success("List created");
      setName("");
      setDescription("");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error("Failed to create list");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this list and all its contacts?")) return;
    try {
      await deleteList(id);
      toast.success("List deleted");
      refresh();
    } catch (e) {
      toast.error("Failed to delete list");
    }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="lists-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">Audience</div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Outfit" }}
          >
            Audience Lists
          </h1>
          <p className="text-sm text-[#475569] mt-2 max-w-xl">
            Upload contacts, keep them organized, and segment your customer base for laser-focused campaigns.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary inline-flex items-center gap-2" data-testid="btn-new-list">
              <Plus size={15} strokeWidth={2.2} />
              New List
            </button>
          </DialogTrigger>
          <DialogContent data-testid="dialog-new-list">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }}>Create audience list</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Name</label>
                <input
                  className="field-input"
                  placeholder="e.g. Q1 Newsletter Subscribers"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="input-list-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Description (optional)</label>
                <input
                  className="field-input"
                  placeholder="Short description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="input-list-description"
                />
              </div>
            </div>
            <DialogFooter>
              <button className="btn-secondary" onClick={() => setOpen(false)} data-testid="btn-cancel-list">
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreate} data-testid="btn-create-list-submit">
                Create List
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Loading lists…</div>
      ) : lists.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-testid="empty-lists">
          <div className="w-14 h-14 rounded-full bg-[#E5E9F6] flex items-center justify-center mx-auto mb-4">
            <Users size={22} className="text-[#002FA7]" strokeWidth={2} />
          </div>
          <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
            No lists yet
          </div>
          <p className="text-sm text-[#475569] mt-1 max-w-md mx-auto">
            Create your first audience list to start uploading contacts via CSV or manual paste.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map((l) => (
            <Link
              key={l.id}
              to={`/lists/${l.id}`}
              data-testid={`list-card-${l.id}`}
              className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:border-[#002FA7] transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md bg-[#E5E9F6] flex items-center justify-center">
                  <Users size={18} className="text-[#002FA7]" strokeWidth={2} />
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(l.id);
                  }}
                  className="p-1.5 rounded-md text-[#94A3B8] hover:text-[#E11D48] hover:bg-[#FFF1F2] transition-colors"
                  data-testid={`btn-delete-list-${l.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="mt-4">
                <div
                  className="text-lg font-semibold tracking-tight text-[#0F172A] group-hover:text-[#002FA7] transition-colors"
                  style={{ fontFamily: "Outfit" }}
                >
                  {l.name}
                </div>
                {l.description ? (
                  <p className="text-xs text-[#94A3B8] mt-1 line-clamp-2">{l.description}</p>
                ) : null}
              </div>
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#F1F5F9]">
                <div>
                  <div className="text-xs text-[#94A3B8] font-medium uppercase tracking-wider">Contacts</div>
                  <div className="text-xl font-semibold text-[#0F172A] tabular-nums" style={{ fontFamily: "Outfit" }}>
                    {l.contact_count}
                  </div>
                </div>
                <ChevronRight size={18} className="text-[#94A3B8] group-hover:text-[#002FA7] transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
